// ---------------------------------------------------------------------------
// YouAreYou (uru) — Shared Utility Functions
//
// Reusable helpers extracted from page components to avoid duplication.
// Import from "@/lib/utils" throughout the project.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Date / Time
// ---------------------------------------------------------------------------

/**
 * Format a Unix timestamp into a human-readable relative time string.
 *
 * Examples: "Just now", "3 minutes ago", "2 hours ago", "Yesterday", "5 days ago"
 */
export function formatRelativeTime(unixTimestamp: number): string {
  const now = Date.now() / 1000;
  const diff = now - unixTimestamp;

  if (diff < 60) return "Just now";
  if (diff < 3600) {
    const mins = Math.floor(diff / 60);
    return `${mins} minute${mins !== 1 ? "s" : ""} ago`;
  }
  if (diff < 86400) {
    const hours = Math.floor(diff / 3600);
    return `${hours} hour${hours !== 1 ? "s" : ""} ago`;
  }
  if (diff < 172800) return "Yesterday";

  const days = Math.floor(diff / 86400);
  if (days < 30) {
    return `${days} day${days !== 1 ? "s" : ""} ago`;
  }

  // Fall back to a formatted date for older items
  const date = new Date(unixTimestamp * 1000);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: date.getFullYear() !== new Date().getFullYear() ? "numeric" : undefined,
  });
}

/**
 * Format a Unix timestamp into a short date string.
 *
 * Examples: "Mar 10", "Jan 5, 2024"
 */
export function formatDate(unixTimestamp: number): string {
  const date = new Date(unixTimestamp * 1000);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: date.getFullYear() !== new Date().getFullYear() ? "numeric" : undefined,
  });
}

/**
 * Format a Unix timestamp into a full date + time string.
 *
 * Example: "Mar 10, 2025 at 2:30 PM"
 */
export function formatDateTime(unixTimestamp: number): string {
  const date = new Date(unixTimestamp * 1000);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }) + " at " + date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

// ---------------------------------------------------------------------------
// String
// ---------------------------------------------------------------------------

/** Capitalize the first letter of a string. */
export function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/**
 * Get initials from a first + last name.
 *
 * Examples: ("Jane", "Smith") → "JS", ("Alice", "") → "A"
 */
export function getInitials(firstName: string, lastName: string): string {
  const first = firstName.charAt(0).toUpperCase();
  const last = lastName.charAt(0).toUpperCase();
  return last ? `${first}${last}` : first || "?";
}

/**
 * Truncate a string to a maximum length, appending "…" if truncated.
 */
export function truncate(str: string, maxLength: number): string {
  if (str.length <= maxLength) return str;
  return str.slice(0, maxLength - 1).trimEnd() + "…";
}

// ---------------------------------------------------------------------------
// Connection Status
// ---------------------------------------------------------------------------

/**
 * Map a connection status to Tailwind text color classes.
 */
export function statusColorClass(status: string): string {
  switch (status) {
    case "verified":
      return "text-verified";
    case "pending":
      return "text-warning";
    case "denied":
    case "cancelled":
      return "text-error";
    default:
      return "text-text-muted";
  }
}

/**
 * Map a connection status to a human-readable label.
 */
export function statusLabel(status: string): string {
  switch (status) {
    case "verified":
      return "Verified";
    case "pending":
      return "Pending";
    case "denied":
      return "Denied";
    case "cancelled":
      return "Cancelled";
    default:
      return capitalize(status);
  }
}

/**
 * Map a connection status to an appropriate Material Symbols icon name.
 */
export function statusIcon(status: string): string {
  switch (status) {
    case "verified":
      return "check_circle";
    case "pending":
      return "schedule";
    case "denied":
      return "cancel";
    case "cancelled":
      return "block";
    default:
      return "help";
  }
}

// ---------------------------------------------------------------------------
// Clipboard
// ---------------------------------------------------------------------------

/**
 * Copy text to the clipboard. Falls back to execCommand for older browsers.
 * Returns true on success, false on failure.
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    // Fallback for older browsers / insecure contexts
    try {
      const textArea = document.createElement("textarea");
      textArea.value = text;
      textArea.style.position = "fixed";
      textArea.style.opacity = "0";
      textArea.style.left = "-9999px";
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      return true;
    } catch {
      return false;
    }
  }
}

// ---------------------------------------------------------------------------
// Phone + Recipient Detection
// ---------------------------------------------------------------------------

/** Matches strings that look like phone numbers: starts with + or digit, then 6+ digits/spaces/dashes/parens. */
export const PHONE_RE = /^[\+\d][\d\s\-\(\)]{6,}$/;

/** Matches strings that look like email addresses. */
export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Detect what kind of recipient value the user has entered.
 *
 * Returns:
 *   "phone"  — looks like a phone number
 *   "email"  — looks like an email address
 *   "label"  — non-empty but neither phone nor email
 *   "empty"  — blank / whitespace only
 */
export function detectRecipientType(
  value: string,
): "phone" | "email" | "label" | "empty" {
  const v = value.trim();
  if (!v) return "empty";
  if (EMAIL_RE.test(v)) return "email";
  if (PHONE_RE.test(v)) return "phone";
  return "label";
}

/**
 * Format a phone number for display. If it's in E.164 format, adds
 * US-style formatting. Otherwise returns as-is.
 *
 * Example: "+15551234567" → "(555) 123-4567"
 */
export function formatPhoneDisplay(phone: string): string {
  // Match US E.164: +1 followed by 10 digits
  const match = phone.match(/^\+1(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    return `(${match[1]}) ${match[2]}-${match[3]}`;
  }
  // Non-US or unrecognized — return as-is
  return phone;
}

// ---------------------------------------------------------------------------
// CSS / Class Names
// ---------------------------------------------------------------------------

/**
 * Merge class name strings, filtering out falsy values.
 * A minimal alternative to the `clsx` package.
 *
 * Example: cn("base", isActive && "active", undefined, "always") → "base active always"
 */
export function cn(...classes: (string | false | null | undefined)[]): string {
  return classes.filter(Boolean).join(" ");
}
